import { useEffect, useState } from 'react';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';

import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { loginUser } from '../../state/actions/authActions';
import { RootState } from '../../state/store';
import { useSnackbar } from "notistack";
import { setCurrentView } from '../../state/reducers/viewReducer';
import { Divider } from '@mui/material';

const Login: React.FC = () => {
  const loginError = useAppSelector((state: RootState) => state.authReducer.error);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const rehydrated = useSelector((s: RootState) => s?._persist?.rehydrated === true);
  const currentView = useSelector((state: RootState) => state.view.currentView);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (rehydrated && currentView !== "Login") {
      dispatch(setCurrentView("Login"));
    }
  }, [currentView, dispatch, rehydrated]);

  const handleLogin = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    setError('');
    if (isWorking) return;
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setIsWorking(true);
    const success = await dispatch(loginUser(email, password));

    if (success) {
      enqueueSnackbar('Login successful.', { variant: 'success', autoHideDuration: 3000 });
    }
    else {
      setError(loginError || 'Login failed. Please check your credentials and try again.');
    }
  };

  return (
    <div className="w-full min-h-screen flex flex-col justify-center gap-6 md:gap-10"
      style={{ backgroundImage: 'url("eclipse-miningware__hero-img.webp")', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}
    >
      <div className="w-auto px-4 text-center fade-up">
        <div className="inline-block glow-radial">
          <img
            src="eclipse-miningware-logo.webp"
            alt="Logo"
            width={200}
            height={74}
            className="mx-auto w-[15rem] sm:w-[20rem] h-auto"
          />
        </div>
      </div>
      <div className="flex items-center justify-center fade-up">
        <div className="bg-white px-8 pb-8 pt-12 shadow-[0_2px_30px_20px_rgba(255,255,255,0.3),0_3px_6px_rgba(0,0,0,0.23)] w-full min-h-48 w-full flex flex-col justify-center lg:max-w-lg lg:pb-10 lg:px-10 lg:pt-14 lg:rounded-lg lg:min-w-[40rem]">
          <div className="flex flex-col items-center">
            <h2 className="text-4xl font-semibold text-center text-theme-primary">Sign In</h2>
            <Divider className="!my-8 w-full max-w-16 !border-theme-border" />
          </div>
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              setIsWorking(true);
              await handleLogin(e);
              setIsWorking(false);
            }}
          >
            <div>
              <input
                type="text"
                name="email"
                placeholder="Email"
                value={email}
                autoComplete="email"
                autoFocus
                disabled={isWorking}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-3 border border-theme-border rounded-md focus:outline-none focus:border-theme-info" />
            </div>
            <div>
              <input
                type="password"
                name="password"
                placeholder="Password"
                value={password}
                disabled={isWorking}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 border border-theme-border rounded-md focus:outline-none focus:border-theme-info" />
            </div>
            <button
              type="submit"
              className="relative w-full bg-theme-primary text-white py-4 rounded-md font-semibold hover:bg-theme-info transition cursor-pointer">
              <span
                className={`absolute text-xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition ${isWorking ? 'opacity-0' : 'opacity-100'}`}
              >
                Login
              </span>
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                className={`mt-1 text-2xl transition ${isWorking ? 'opacity-100' : 'opacity-0'}`}
              />
            </button>
          </form>
          <div className="min-h-10">
            {error && <p className="text-theme-danger mt-4 animate-fade-in">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
